import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { adminApi, type QuestTier, type QuestTierOption } from '@/lib/adminApi';
import { Card, DataTable, Spinner, EmptyState, Badge, StatTile, type Column } from '@/components/ui';
import { IconAward } from '@/components/ui/icons';
import { AdminPage, AdminPageHeader } from '@/components/admin/shared';
import { Drawer, Field } from '@/components/admin/Drawer';
import { Pager } from '@/components/admin/Pager';

const PAGE = 50;

/** Quest Tiers — the ordered bonus ladder for each driver quest (every quest has
 *  tiers: a trip target → a bonus payout). Covers the legacy Filament
 *  DriverQuestTierResource (columns: quest, tier order, trips required, bonus;
 *  filter: by quest).
 *
 *  READ-ONLY. incentives-svc OWNS `driver_quests` + `driver_quest_tiers` (it
 *  serves them to the driver app's Opportunities feed); admin-svc reads them
 *  through its shared read pool, exactly like the Incentives campaigns + loyalty
 *  tiers views. There is no admin authoring path for quest tiers yet —
 *  incentives-svc exposes an internal write endpoint for campaigns (which the
 *  Incentives page proxies) but not for quest tiers — so this is a faithful
 *  list/detail read, clearly labelled as such. Full CRUD would need an
 *  incentives-svc authoring endpoint for tiers + a proxy in admin-svc (mirroring
 *  incentive_author.go). The section is DEFENSIVE: it shows a clean "not yet
 *  available" state when the incentives tables aren't in this Postgres yet. */
export function QuestTiers() {
  const [offset, setOffset] = useState(0);
  // '' = all quests; otherwise the selected quest id (as a string for the <select>).
  const [questId, setQuestId] = useState('');
  const [selected, setSelected] = useState<QuestTier | null>(null);

  const { data, isLoading, isError, error, isFetching } = useQuery({
    queryKey: ['admin', 'quest-tiers', questId, offset],
    queryFn: ({ signal }) =>
      adminApi.questTiers({ quest_id: questId ? Number(questId) : undefined, limit: PAGE, offset }, signal),
    placeholderData: (prev) => prev,
  });

  const unavailable = data && data.available === false;
  const rows = useMemo(() => data?.items ?? [], [data]);
  const quests = data?.quests ?? [];

  // KPI roll-up across the loaded page: distinct quests + the total bonus on
  // offer (the sum every tier's payout — what a driver clearing the whole ladder
  // of every listed quest would earn). Computed from the rows in view.
  const stats = useMemo(() => summarize(rows), [rows]);

  return (
    <AdminPage>
      <AdminPageHeader
        title="Quest Tiers"
        subtitle="The bonus ladder for each driver quest — trip targets and their payouts (read-only)."
        right={
          quests.length > 0 ? (
            <QuestFilter
              quests={quests}
              value={questId}
              onChange={(v) => {
                setQuestId(v);
                setOffset(0);
              }}
            />
          ) : undefined
        }
      />

      {unavailable ? (
        <Card>
          <EmptyState
            title="Quest tiers not yet in Postgres"
            description={
              data?.note ??
              'The driver_quest_tiers table is owned by incentives-svc and is not in this Postgres yet.'
            }
            icon={<IconAward className="h-6 w-6" />}
          />
        </Card>
      ) : (
        <>
          <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
            <StatTile label="Tiers" value={data?.total ?? 0} hint={tierHint(questId, quests)} />
            <StatTile label="Quests with tiers" value={quests.length} tone="info" />
            <StatTile
              label="Total bonus on offer"
              value={`$${stats.totalBonus.toFixed(2)}`}
              tone="ok"
              hint="Sum of every payout in view"
            />
          </div>

          <Card flush>
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Spinner label="Loading quest tiers…" />
              </div>
            ) : isError ? (
              <EmptyState title="Couldn't load quest tiers" description={(error as Error)?.message} />
            ) : (
              <>
                <QuestTiersTable rows={rows} onView={(t) => setSelected(t)} />
                <Pager total={data?.total ?? 0} offset={offset} pageSize={PAGE} onOffset={setOffset} fetching={isFetching} />
              </>
            )}
          </Card>

          <p className="mt-3 text-[11px] text-text-muted">
            Read-only view. Quest tiers are authored in incentives-svc (the driver Opportunities feed reads them);
            this page mirrors the legacy Quest Tiers admin for visibility.
          </p>
        </>
      )}

      {selected !== null ? <QuestTierDrawer tier={selected} onClose={() => setSelected(null)} /> : null}
    </AdminPage>
  );
}

/** The "filter by quest" dropdown shown in the page header. Mirrors the legacy
 *  SelectFilter (options = quests ordered by title). '' = all quests. */
function QuestFilter({
  quests,
  value,
  onChange,
}: {
  quests: QuestTierOption[];
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="input w-56"
      aria-label="Filter by quest"
    >
      <option value="">All quests</option>
      {quests.map((q) => (
        <option key={q.id} value={String(q.id)}>
          {q.title}
        </option>
      ))}
    </select>
  );
}

/** The quest-tiers DataTable. Pulled out so the page body stays readable. Each
 *  row carries a "View" button that opens the read-only detail drawer. */
function QuestTiersTable({ rows, onView }: { rows: QuestTier[]; onView: (t: QuestTier) => void }) {
  const columns: Array<Column<QuestTier>> = [
    { key: 'id', header: 'ID', cell: (r) => <span className="metric text-xs text-text-muted">#{r.id}</span> },
    {
      key: 'quest',
      header: 'Quest',
      cell: (r) => <span className="font-medium text-slate-100">{questLabel(r)}</span>,
    },
    {
      key: 'order',
      header: 'Tier',
      cell: (r) => <Badge tone="info">{`Tier ${r.tier_order + 1}`}</Badge>,
    },
    {
      key: 'trips',
      header: 'Trips required',
      align: 'right',
      cell: (r) => <span className="metric text-sm text-slate-200">{r.trips_required}</span>,
    },
    {
      key: 'bonus',
      header: 'Bonus',
      align: 'right',
      cell: (r) => <span className="metric text-sm text-ok">${r.bonus_amount.toFixed(2)}</span>,
    },
    {
      key: 'view',
      header: '',
      align: 'right',
      cell: (r) => (
        <button
          type="button"
          onClick={() => onView(r)}
          className="rounded-md border border-ink-700 px-2 py-1 text-xs text-brand-400 hover:bg-ink-800"
        >
          View
        </button>
      ),
    },
  ];

  return (
    <DataTable<QuestTier>
      columns={columns}
      rows={rows}
      rowKey={(r) => String(r.id)}
      stickyHeader
      empty={
        <EmptyState
          title="No quest tiers"
          description="No driver quest tiers match this filter."
          icon={<IconAward className="h-6 w-6" />}
        />
      }
    />
  );
}

/** Read-only detail drawer for one tier. No edit/delete controls — quest-tier
 *  authoring lives in incentives-svc (see the page note). */
function QuestTierDrawer({ tier, onClose }: { tier: QuestTier; onClose: () => void }) {
  return (
    <Drawer title={`Tier ${tier.tier_order + 1}`} subtitle={questLabel(tier)} onClose={onClose}>
      <div className="space-y-4">
        <Card>
          <div className="space-y-0.5">
            <Field label="Tier ID" value={`#${tier.id}`} />
            <Field label="Quest" value={questLabel(tier)} />
            <Field label="Quest ID" value={`#${tier.quest_id}`} />
            <Field label="Tier order (raw)" value={String(tier.tier_order)} />
            <Field label="Trips required" value={String(tier.trips_required)} />
            <Field label="Bonus" value={`$${tier.bonus_amount.toFixed(2)}`} />
          </div>
        </Card>

        <div className="rounded-lg border border-ink-700 bg-ink-850 p-3">
          <p className="text-xs text-text-secondary">
            A driver who completes <span className="metric text-slate-200">{tier.trips_required}</span> qualifying
            trips on this quest earns a <span className="metric text-ok">${tier.bonus_amount.toFixed(2)}</span> bonus.
          </p>
        </div>

        <p className="text-[11px] text-text-muted">
          Read-only. Quest tiers are managed in incentives-svc.
        </p>
      </div>
    </Drawer>
  );
}

// ─── helpers ─────────────────────────────────────────────────

/** The display label for a tier's quest: its title, or a stable "Quest #<id>"
 *  fallback (the server already labels orphaned/untitled quests this way, but we
 *  guard client-side too). */
function questLabel(t: QuestTier): string {
  return t.quest_title && t.quest_title.trim() !== '' ? t.quest_title : `Quest #${t.quest_id}`;
}

/** The hint under the "Tiers" KPI — reflects whether a quest filter is active. */
function tierHint(questId: string, quests: QuestTierOption[]): string {
  if (!questId) return 'Across all quests';
  const q = quests.find((x) => String(x.id) === questId);
  return q ? q.title : 'Filtered';
}

/** Roll up the loaded page into the KPI figures (the total bonus on offer). */
function summarize(rows: QuestTier[]): { totalBonus: number } {
  const totalBonus = rows.reduce((sum, r) => sum + (Number.isFinite(r.bonus_amount) ? r.bonus_amount : 0), 0);
  return { totalBonus };
}
